import React from 'react'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import LegalContent from 'src/components-new/layout/LegalContent'
import PasswordFieldNew from 'src/components-new/inputs/PasswordFieldNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import apiClient from 'src/services/apiClient'
import {ConsentOption} from '../selfRegistrationValidation'

const CONSENT_OPTIONS = [
  {value: 'CONSENT', label: 'Esprimo il consenso'},
  {value: 'NOT_CONSENT', label: 'Nego il consenso'},
]

export interface ConsentsStepValue {
  consents: Record<string, string>
  consentPassword: string
}

interface FirstLoginConfiguration {
  termsAndConditions?: string
  dataProcessingInfo?: string
  consentCheckboxPreText?: string
  consentCheckboxPostText?: string
  consentCheckboxOptions?: ConsentOption[]
  cookieText?: string
  cookieFile?: {file: string, name?: string}
}

interface ConsentsStepProps {
  firstLoginConfiguration?: FirstLoginConfiguration
  value: ConsentsStepValue
  onChange: (value: ConsentsStepValue) => void
  errors: Record<string, string>
  disabled?: boolean
}

/**
 * Step 3/3 — new-style equivalent of the legacy inner step 1 of
 * src/pages/selfRegistration/steps/SecondStep/SecondStep.tsx:401-483. The
 * termsAndConditions/dataProcessingInfo HTML + per-option CONSENT/NOT_CONSENT radio block
 * mirrors src/pages-new/consentsAcceptance/ConsentsAcceptancePageNew.tsx exactly (same
 * validation messages, same raw-MUI FormControl/RadioGroup — not `RadioGroupNew`, which
 * has no `error` styling on the radios themselves, only on the helper text).
 * `consentPassword` re-confirms the password chosen in the previous step (validated by
 * the parent container against `formState.personalData.password`).
 */
export default function ConsentsStep({
  firstLoginConfiguration,
  value,
  onChange,
  errors,
  disabled,
}: ConsentsStepProps): JSX.Element {
  const firstLogin = firstLoginConfiguration ?? {}
  const options: ConsentOption[] = firstLogin.consentCheckboxOptions ?? []

  const setConsent = (optionValue: string, consentValue: string) => {
    onChange({...value, consents: {...value.consents, [optionValue]: consentValue}})
  }

  const downloadCookieFile = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!firstLogin.cookieFile?.file) return
    try {
      const response = await apiClient.getCookieFile(firstLogin.cookieFile.file, () => undefined)
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = firstLogin.cookieFile.name || 'cookie-policy.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch {
      // Best-effort download — same swallow-and-ignore approach as
      // ConsentsAcceptancePageNew.tsx's downloadCookieFile.
    }
  }

  return (
    <Stack spacing={3}>
      <LegalContent html={firstLogin.termsAndConditions ?? ''} />

      {(firstLogin.cookieText || firstLogin.cookieFile?.file) && (
        <Card title="Cookie policy">
          <Typography variant="body2" sx={{color: NEUTRAL_SCALE[900]}}>
            {firstLogin.cookieText}
            {firstLogin.cookieFile?.file && (
              <>
                {' '}
                <Typography
                  component="span"
                  onClick={downloadCookieFile}
                  sx={{color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit'}}
                >
                  {'cookie policy.'}
                </Typography>
              </>
            )}
          </Typography>
        </Card>
      )}

      <LegalContent html={firstLogin.dataProcessingInfo ?? ''} />

      <Typography variant="body1" sx={{fontWeight: 700}}>
        {'CONSENSO AL TRATTAMENTO DEI DATI PERSONALI'}
      </Typography>
      {firstLogin.consentCheckboxPreText && (
        <Typography variant="body2">{firstLogin.consentCheckboxPreText}</Typography>
      )}

      <Stack spacing={2}>
        {options.map((opt) => (
          <FormControl key={opt.value} error={Boolean(errors[opt.value])} component="fieldset">
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[900], mb: 0.5, fontWeight: 500}}>
              {opt.label}
            </Typography>
            <RadioGroup
              value={value.consents[opt.value] ?? ''}
              onChange={(_, v) => setConsent(opt.value, v)}
            >
              {CONSENT_OPTIONS.map((co) => (
                <FormControlLabel
                  key={co.value}
                  value={co.value}
                  control={<Radio disabled={disabled} />}
                  label={co.label}
                />
              ))}
            </RadioGroup>
            {errors[opt.value] && <FormHelperText>{errors[opt.value]}</FormHelperText>}
          </FormControl>
        ))}
      </Stack>

      {firstLogin.consentCheckboxPostText && (
        <Typography variant="body2">{firstLogin.consentCheckboxPostText}</Typography>
      )}

      <PasswordFieldNew
        label="Conferma la password"
        value={value.consentPassword}
        onChange={(consentPassword) => onChange({...value, consentPassword})}
        autoComplete="new-password"
        required
        fullWidth
        disabled={disabled}
        error={Boolean(errors.consentPassword)}
        helperText={errors.consentPassword || 'Reinserisci la password scelta per confermare i consensi'}
      />
    </Stack>
  )
}
